import axios from 'axios'
import { Location } from 'history'
import queryString from 'query-string'
import React from 'react'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { ExtendAnalyticsBrowser } from 'src/analytics/AnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { AddContactsToastMessage } from 'src/app/Toast/Toast'
import { PeopleDetailInfoFields } from 'src/components/PeopleDetailInfoForm/PeopleDetailInfoForm'
import { CspUserListResponse } from 'src/models/cspUser.models'
import { PaginationResponse } from 'src/models/pagination.models'
import {
  UserDepartment,
  UserListModel,
  UserListResponse,
} from 'src/models/user.models'
import { ScreeningResponse } from 'src/models/virtualCard.models'
import { closeModal } from 'src/navigation/Lnk'
import { homeLocation } from 'src/navigation/locations'
import { virtualCardsQueryKeys } from 'src/queries/queries.constants'
import { queryClient } from 'src/queryClient'
import { RootState } from 'src/redux/createRootReducer'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectAuthedFormApiRequestConfig,
  selectAuthedJsonApiRequestConfig,
  selectCspAuthedIssuerIdJsonApiRequestConfig,
} from 'src/redux/selectors/axios.selectors'
import { GetUsersSearchRequest } from 'src/requests/getUsers.request'
import { PatchUserRequest } from 'src/requests/patchUser.request'
import { PostUserInvitesRequest } from 'src/requests/postUserInvites.request'
import { PostUserScreenRequest } from 'src/requests/postUserScreen.request'
import { BackendErrorPayload } from 'src/utils/error.utils'

export const PATCH_USER_INIT = 'data/users/PATCH_USER_INIT'
export const PATCH_USER_PROFILE_INIT = 'data/users/PATCH_USER_PROFILE_INIT'
export const PATCH_USER_LOCALE_INIT = 'data/users/PATCH_USER_LOCALE_INIT'
export const PATCH_USER_CURRENCY_INIT = 'data/users/PATCH_USER_CURRENCY_INIT'
export const PATCH_USER_EMPLOYEE_INIT = 'data/users/PATCH_USER_EMPLOYEE_INIT'
export const PATCH_USER_SUCCESS = 'data/users/PATCH_USER_SUCCESS'
export const PATCH_USER_FAILURE = 'data/users/PATCH_USER_FAILURE'
export const UPDATE_MEMBER_DEPARTMENT = 'data/users/UPDATE_MEMBER_DEPARTMENT'

export type PatchUserSuccessPayload = UserListResponse

export interface PatchUserInitAction {
  type: typeof PATCH_USER_INIT
}
export interface PatchUserProfileInitAction {
  type: typeof PATCH_USER_PROFILE_INIT
}
export interface PatchUserLocaleInitAction {
  type: typeof PATCH_USER_LOCALE_INIT
}
export interface PatchUserCurrencyInitAction {
  type: typeof PATCH_USER_CURRENCY_INIT
}
export interface PatchUserEmployeeInitAction {
  type: typeof PATCH_USER_EMPLOYEE_INIT
}
export interface PatchUserSuccessAction {
  type: typeof PATCH_USER_SUCCESS
  payload: PatchUserSuccessPayload
}
export interface PatchUserFailureAction {
  type: typeof PATCH_USER_FAILURE
  payload: BackendErrorPayload<string>
}
export interface UpdateMemberDepartmentAction {
  type: typeof UPDATE_MEMBER_DEPARTMENT
  payload: {
    userIds: string[]
    department: UserDepartment
  }
}

export type PatchUserActions =
  | PatchUserInitAction
  | PatchUserProfileInitAction
  | PatchUserLocaleInitAction
  | PatchUserCurrencyInitAction
  | PatchUserEmployeeInitAction
  | PatchUserSuccessAction
  | PatchUserFailureAction

const createPatchUserInitAction = (): PatchUserInitAction => ({
  type: PATCH_USER_INIT,
})
const createPatchUserProfileInitAction = (): PatchUserProfileInitAction => ({
  type: PATCH_USER_PROFILE_INIT,
})
const createPatchUserLocaleInitAction = (): PatchUserLocaleInitAction => ({
  type: PATCH_USER_LOCALE_INIT,
})
const createPatchUserCurrencyInitAction = (): PatchUserCurrencyInitAction => ({
  type: PATCH_USER_CURRENCY_INIT,
})
const createPatchUserEmployeeInitAction = (): PatchUserEmployeeInitAction => ({
  type: PATCH_USER_EMPLOYEE_INIT,
})
const createPatchUserSuccessAction = (
  payload: PatchUserSuccessPayload,
): PatchUserSuccessAction => ({
  type: PATCH_USER_SUCCESS,
  payload,
})
const createPatchUserFailureAction = (
  payload: BackendErrorPayload<string>,
): PatchUserFailureAction => ({
  type: PATCH_USER_FAILURE,
  payload,
})

export const patchUser = (
  body: Partial<PatchUserRequest>,
  action: PatchUserActions,
): ThunkAction<
  Promise<PatchUserFailureAction | PatchUserSuccessAction>,
  RootState,
  void,
  PatchUserActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PatchUserActions>,
    getState: () => RootState,
  ): Promise<PatchUserFailureAction | PatchUserSuccessAction> => {
    switch (action.type) {
      case PATCH_USER_PROFILE_INIT:
        dispatch(createPatchUserProfileInitAction())
        break
      case PATCH_USER_LOCALE_INIT:
        dispatch(createPatchUserLocaleInitAction())
        break
      case PATCH_USER_CURRENCY_INIT:
        dispatch(createPatchUserCurrencyInitAction())
        break
      case PATCH_USER_EMPLOYEE_INIT:
        dispatch(createPatchUserEmployeeInitAction())
        break
      default:
        dispatch(createPatchUserInitAction())
    }
    return axios
      .patch<PatchUserSuccessPayload>(
        `/users`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(createPatchUserSuccessAction(response.data))
        },
        (err) => {
          return dispatch(
            createPatchUserFailureAction(
              err.response?.data ?? { error: 'An unexpected error occurred.' },
            ),
          )
        },
      )
  }
}

export const POST_USER_SCREEN_INIT = 'data/users/POST_USER_SCREEN_INIT'
export const POST_USER_SCREEN_SUCCESS = 'data/users/POST_USER_SCREEN_SUCCESS'
export const POST_USER_SCREEN_FAILURE = 'data/users/POST_USER_SCREEN_FAILURE'

export type PostUserScreenSuccessPayload = ScreeningResponse

export interface PostUserScreenInitAction {
  type: typeof POST_USER_SCREEN_INIT
}
export interface PostUserScreenSuccessAction {
  type: typeof POST_USER_SCREEN_SUCCESS
  payload: PostUserScreenSuccessPayload
}
export interface PostUserScreenFailureAction {
  type: typeof POST_USER_SCREEN_FAILURE
  payload: BackendErrorPayload<string>
}

export type PostUserScreenActions =
  | PostUserScreenInitAction
  | PostUserScreenSuccessAction
  | PostUserScreenFailureAction

const createPostUserScreenInitAction = (): PostUserScreenInitAction => ({
  type: POST_USER_SCREEN_INIT,
})
const createPostUserScreenSuccessAction = (
  payload: PostUserScreenSuccessPayload,
): PostUserScreenSuccessAction => ({
  type: POST_USER_SCREEN_SUCCESS,
  payload,
})
const createPostUserScreenFailureAction = (
  payload: BackendErrorPayload<string>,
): PostUserScreenFailureAction => ({
  type: POST_USER_SCREEN_FAILURE,
  payload,
})

export const postUserScreen = (
  body: Partial<PostUserScreenRequest>,
  virtualCardId: string,
): ThunkAction<
  Promise<PostUserScreenFailureAction | PostUserScreenSuccessAction>,
  RootState,
  void,
  PostUserScreenActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostUserScreenActions>,
    getState: () => RootState,
  ): Promise<PostUserScreenFailureAction | PostUserScreenSuccessAction> => {
    dispatch(createPostUserScreenInitAction())
    return axios
      .post<PostUserScreenSuccessPayload>(
        `/users/screen`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          await queryClient.invalidateQueries({
            queryKey: virtualCardsQueryKeys.detail(virtualCardId),
          })
          return dispatch(createPostUserScreenSuccessAction(response.data))
        },
        (err) => {
          return dispatch(createPostUserScreenFailureAction(err.response.data))
        },
      )
  }
}

export const DELETE_USER_INIT = 'data/users/DELETE_USER_INIT'
export const DELETE_USER_SUCCESS = 'data/users/DELETE_USER_SUCCESS'
export const DELETE_USER_FAILURE = 'data/users/DELETE_USER_FAILURE'

export type DeleteUserSuccessPayload = {
  authedRedirectLocation: Location
}
export type DeleteUserFailurePayload = BackendErrorPayload<string>

export interface DeleteUserInitAction {
  type: typeof DELETE_USER_INIT
}
export interface DeleteUserSuccessAction {
  type: typeof DELETE_USER_SUCCESS
  payload: DeleteUserSuccessPayload
}
export interface DeleteUserFailureAction {
  type: typeof DELETE_USER_FAILURE
  payload: DeleteUserFailurePayload
}

export type DeleteUserActions =
  | DeleteUserInitAction
  | DeleteUserSuccessAction
  | DeleteUserFailureAction

const createDeleteUserInitAction = (): DeleteUserInitAction => ({
  type: DELETE_USER_INIT,
})
const createDeleteUserSuccessAction = (
  payload: DeleteUserSuccessPayload,
): DeleteUserSuccessAction => ({
  type: DELETE_USER_SUCCESS,
  payload,
})
const createDeleteUserFailureAction = (
  payload: DeleteUserFailurePayload,
): DeleteUserFailureAction => ({
  type: DELETE_USER_FAILURE,
  payload,
})

export const deleteUser = (
  analytics?: ExtendAnalyticsBrowser,
): ThunkAction<Promise<void>, RootState, void, DeleteUserActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, DeleteUserActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createDeleteUserInitAction())
    return axios
      .delete(`/users/me`, selectAuthedJsonApiRequestConfig(getState()))
      .then(
        async (response) => {
          dispatch(
            createDeleteUserSuccessAction({
              ...response.data,
              authedRedirectLocation: homeLocation(),
            }),
          )
          analytics?.track(
            'delete profile - permanently delete profile - button press',
            {
              interaction: 'button press',
              intent: 'Permanently Delete Profile',
            },
          )
        },
        (err) => {
          dispatch(createDeleteUserFailureAction(err.response.data))
        },
      )
  }
}

export const POST_USER_AVATAR_INIT = 'data/users/POST_USER_AVATAR_INIT'
export const POST_USER_AVATAR_SUCCESS = 'data/users/POST_USER_AVATAR_SUCCESS'
export const POST_USER_AVATAR_FAILURE = 'data/users/POST_USER_AVATAR_FAILURE'

export type PostUserAvatarSuccessPayload = {
  user: UserListModel
}

export type PostUserAvatarFailurePayload = BackendErrorPayload<string> | string

export interface PostUserAvatarInitAction {
  type: typeof POST_USER_AVATAR_INIT
}
export interface PostUserAvatarSuccessAction {
  type: typeof POST_USER_AVATAR_SUCCESS
  payload: PostUserAvatarSuccessPayload
}
export interface PostUserAvatarFailureAction {
  type: typeof POST_USER_AVATAR_FAILURE
  payload: PostUserAvatarFailurePayload
}

export type PostUserAvatarActions =
  | PostUserAvatarInitAction
  | PostUserAvatarSuccessAction
  | PostUserAvatarFailureAction

const createPostUserAvatarInitAction = (): PostUserAvatarInitAction => ({
  type: POST_USER_AVATAR_INIT,
})
const createPostUserAvatarSuccessAction = (
  payload: PostUserAvatarSuccessPayload,
): PostUserAvatarSuccessAction => ({
  type: POST_USER_AVATAR_SUCCESS,
  payload,
})
const createPostUserAvatarFailureAction = (
  payload: PostUserAvatarFailurePayload,
): PostUserAvatarFailureAction => ({
  type: POST_USER_AVATAR_FAILURE,
  payload,
})

export const postUserAvatar = (
  formData: FormData,
): ThunkAction<Promise<void>, RootState, void, PostUserAvatarActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostUserAvatarActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostUserAvatarInitAction())
    return axios
      .post<PostUserAvatarSuccessPayload>(
        `/useravatars`,
        formData,
        selectAuthedFormApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPostUserAvatarSuccessAction(response.data))
        },
        (err) => {
          dispatch(
            createPostUserAvatarFailureAction(
              err?.response?.data !== undefined
                ? err.response.data
                : err.message,
            ),
          )
        },
      )
  }
}

export const POST_USER_INVITES_INIT = 'data/users/POST_USER_INVITES_INIT'
export const POST_USER_INVITES_SUCCESS = 'data/users/POST_USER_INVITES_SUCCESS'
export const POST_USER_INVITES_FAILURE = 'data/users/POST_USER_INVITES_FAILURE'

export type PostUserInvitesSuccessPayload = {
  users: UserListResponse[]
}

export type PostUserInvitesErrorPayload = BackendErrorPayload<string>

export interface PostUserInvitesInitAction {
  type: typeof POST_USER_INVITES_INIT
}
export interface PostUserInvitesSuccessAction {
  type: typeof POST_USER_INVITES_SUCCESS
  payload: PostUserInvitesSuccessPayload
}
export interface PostUserInvitesFailureAction {
  type: typeof POST_USER_INVITES_FAILURE
  payload: PostUserInvitesErrorPayload
}

export type PostUserInvitesActions =
  | PostUserInvitesInitAction
  | PostUserInvitesSuccessAction
  | PostUserInvitesFailureAction

const createPostUserInvitesInitAction = (): PostUserInvitesInitAction => ({
  type: POST_USER_INVITES_INIT,
})
const createPostUserInvitesSuccessAction = (
  payload: PostUserInvitesSuccessPayload,
): PostUserInvitesSuccessAction => ({
  type: POST_USER_INVITES_SUCCESS,
  payload,
})
const createPostUserInvitesFailureAction = (
  payload: PostUserInvitesErrorPayload,
): PostUserInvitesFailureAction => ({
  type: POST_USER_INVITES_FAILURE,
  payload,
})

export const postUserInvitesAndOpenToastAndCloseFlyout = (
  body: Partial<PostUserInvitesRequest>,
): ThunkAction<Promise<void>, RootState, void, PostUserInvitesActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostUserInvitesActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostUserInvitesInitAction())
    return axios
      .post<PostUserInvitesSuccessPayload>(
        `/users/invite`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPostUserInvitesSuccessAction(response.data))
          dispatch(closeModal())
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <AddContactsToastMessage users={response.data.users} />
              ),
            }),
          )
        },
        (err) => {
          dispatch(createPostUserInvitesFailureAction(err.response.data))
        },
      )
  }
}

export const GET_USERS_INIT = 'data/users/GET_USERS_INIT'
export const GET_USERS_SUCCESS = 'data/users/GET_USERS_SUCCESS'
export const GET_USERS_FAILURE = 'data/users/GET_USERS_FAILURE'

export interface GetUsersSuccessPayload {
  users: UserListResponse[]
  pagination: PaginationResponse
}

export interface GetUsersInitAction {
  type: typeof GET_USERS_INIT
}
export interface GetUsersSuccessAction {
  type: typeof GET_USERS_SUCCESS
  payload: GetUsersSuccessPayload
}
export interface GetUsersFailureAction {
  type: typeof GET_USERS_FAILURE
  payload: unknown
}

export type GetUsersActions =
  | GetUsersInitAction
  | GetUsersSuccessAction
  | GetUsersFailureAction
  | AddOwnUserAction

const createGetUsersInitAction = (): GetUsersInitAction => ({
  type: GET_USERS_INIT,
})
const createGetUsersSuccessAction = (
  payload: GetUsersSuccessPayload,
): GetUsersSuccessAction => ({
  type: GET_USERS_SUCCESS,
  payload,
})
const createGetUsersFailureAction = (err: unknown): GetUsersFailureAction => ({
  type: GET_USERS_FAILURE,
  payload: err,
})

export const getUsers = (
  queryParameters: GetUsersSearchRequest,
  getOwnUser?: boolean,
): ThunkAction<
  Promise<GetUsersSuccessAction | GetUsersFailureAction | AddOwnUserAction>,
  RootState,
  void,
  GetUsersActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetUsersActions>,
    getState: () => RootState,
  ): Promise<
    GetUsersSuccessAction | GetUsersFailureAction | AddOwnUserAction
  > => {
    const loggedInUser = selectUserListModelFromState(getState())
    dispatch(createGetUsersInitAction())
    return axios
      .get<GetUsersSuccessPayload>(
        `/users/search?${queryString.stringify(queryParameters)}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          if (getOwnUser && loggedInUser) {
            dispatch(createGetUsersSuccessAction(response?.data))
            return dispatch(createAddOwnUserAction(loggedInUser.id))
          } else {
            return dispatch(createGetUsersSuccessAction(response?.data))
          }
        },
        (err) => {
          return dispatch(createGetUsersFailureAction(err.response?.data))
        },
      )
  }
}

export const GET_OWN_USER_INIT = 'data/users/GET_OWN_USER_INIT'
export const GET_OWN_USER_SUCCESS = 'data/users/GET_OWN_USER_SUCCESS'
export const GET_OWN_USER_FAILURE = 'data/users/GET_OWN_USER_FAILURE'

export interface GetOwnUserAction {
  type: typeof GET_OWN_USER_INIT
}
export interface GetOwnUserSuccessAction {
  type: typeof GET_OWN_USER_SUCCESS
  payload: UserListResponse
}
export interface GetOwnUserFailureAction {
  type: typeof GET_OWN_USER_FAILURE
}

export type GetOwnUserActions =
  | GetOwnUserAction
  | GetOwnUserSuccessAction
  | GetOwnUserFailureAction

const createGetOwnUserAction = (): GetOwnUserAction => ({
  type: GET_OWN_USER_INIT,
})
const createGetOwnUserSuccessAction = (
  payload: UserListResponse,
): GetOwnUserSuccessAction => ({
  type: GET_OWN_USER_SUCCESS,
  payload: payload,
})
const createGetOwnUserFailureAction = (): GetOwnUserFailureAction => ({
  type: GET_OWN_USER_FAILURE,
})

export const getOwnUser = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetOwnUserActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetOwnUserActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetOwnUserAction())
    const state = getState()
    return axios.get('/users/me', selectAuthedJsonApiRequestConfig(state)).then(
      (response) => {
        dispatch(createGetOwnUserSuccessAction(response.data))
      },
      () => {
        dispatch(createGetOwnUserFailureAction())
      },
    )
  }
}

export const GET_RECENT_USERS_INIT = 'data/users/GET_RECENT_USERS_INIT'
export const GET_RECENT_USERS_SUCCESS = 'data/users/GET_RECENT_USERS_SUCCESS'
export const GET_RECENT_USERS_FAILURE = 'data/users/GET_RECENT_USERS_FAILURE'

export interface GetRecentUsersSuccessPayload {
  users: UserListResponse[]
  pagination: PaginationResponse
}

export interface GetRecentUsersInitAction {
  type: typeof GET_RECENT_USERS_INIT
}
export interface GetRecentUsersSuccessAction {
  type: typeof GET_RECENT_USERS_SUCCESS
  payload: GetRecentUsersSuccessPayload
  userAssociation: string
}
export interface GetRecentUsersFailureAction {
  type: typeof GET_RECENT_USERS_FAILURE
  payload: unknown
}

export type GetRecentUsersActions =
  | GetRecentUsersInitAction
  | GetRecentUsersSuccessAction
  | GetRecentUsersFailureAction

const createGetRecentUsersInitAction = (): GetRecentUsersInitAction => ({
  type: GET_RECENT_USERS_INIT,
})
const createGetRecentUsersSuccessAction = (
  payload: GetRecentUsersSuccessPayload,
  userAssociation: string,
): GetRecentUsersSuccessAction => ({
  type: GET_RECENT_USERS_SUCCESS,
  payload,
  userAssociation,
})
const createGetRecentUsersFailureAction = (
  err: unknown,
): GetRecentUsersFailureAction => ({
  type: GET_RECENT_USERS_FAILURE,
  payload: err,
})

export const getRecentUsers = (
  userAssociation: string,
  count?: number,
): ThunkAction<
  Promise<GetRecentUsersSuccessAction | GetRecentUsersFailureAction>,
  RootState,
  void,
  GetRecentUsersActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetRecentUsersActions>,
    getState: () => RootState,
  ): Promise<GetRecentUsersSuccessAction | GetRecentUsersFailureAction> => {
    dispatch(createGetRecentUsersInitAction())
    return axios
      .get<GetRecentUsersSuccessPayload>(
        `/users/recent?associatedBy=` +
          userAssociation +
          (count !== undefined ? `&count=` + count : ``),
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          return dispatch(
            createGetRecentUsersSuccessAction(response.data, userAssociation),
          )
        },
        (err) => {
          return dispatch(createGetRecentUsersFailureAction(err.response.data))
        },
      )
  }
}

export const GET_USER_INIT = 'data/users/GET_USER_INIT'
export const GET_USER_SUCCESS = 'data/users/GET_USER_SUCCESS'
export const GET_USER_FAILURE = 'data/users/GET_USER_FAILURE'

export type GetUserSuccessPayload = UserListResponse

export interface GetUserInitAction {
  type: typeof GET_USER_INIT
}
export interface GetUserSuccessAction {
  type: typeof GET_USER_SUCCESS
  payload: GetUserSuccessPayload
}
export interface GetUserFailureAction {
  type: typeof GET_USER_FAILURE
  payload: unknown
}

export type GetUserActions =
  | GetUserInitAction
  | GetUserSuccessAction
  | GetUserFailureAction

const createGetUserInitAction = (): GetUserInitAction => ({
  type: GET_USER_INIT,
})
const createGetUserSuccessAction = (
  payload: GetUserSuccessPayload,
): GetUserSuccessAction => ({
  type: GET_USER_SUCCESS,
  payload,
})
const createGetUserFailureAction = (
  response: unknown,
): GetUserFailureAction => ({
  type: GET_USER_FAILURE,
  payload: response,
})

export const getUser = (
  userId: string,
): ThunkAction<
  Promise<GetUserSuccessAction | GetUserFailureAction>,
  RootState,
  void,
  GetUserActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetUserActions>,
    getState: () => RootState,
  ): Promise<GetUserSuccessAction | GetUserFailureAction> => {
    dispatch(createGetUserInitAction())
    return axios
      .get<GetUserSuccessPayload>(
        `/users/${userId}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(createGetUserSuccessAction(response.data))
        },
        async (err) => {
          return dispatch(createGetUserFailureAction(err.response.data))
        },
      )
  }
}

export const GET_CSP_USER_INIT = 'data/users/GET_CSP_USER_INIT'
export const GET_CSP_USER_SUCCESS = 'data/users/GET_CSP_USER_SUCCESS'
export const GET_CSP_USER_FAILURE = 'data/users/GET_CSP_USER_FAILURE'

export type GetCspUserSuccessPayload = {
  id: string
  user: CspUserListResponse
}

export interface GetCspUserInitAction {
  type: typeof GET_CSP_USER_INIT
}
export interface GetCspUserSuccessAction {
  type: typeof GET_CSP_USER_SUCCESS
  payload: GetCspUserSuccessPayload
}
export interface GetCspUserFailureAction {
  type: typeof GET_CSP_USER_FAILURE
  payload: unknown
}

export type GetCspUserActions =
  | GetCspUserInitAction
  | GetCspUserSuccessAction
  | GetCspUserFailureAction

const createGetCspUserInitAction = (): GetCspUserInitAction => ({
  type: GET_CSP_USER_INIT,
})
const createGetCspUserSuccessAction = (
  payload: GetCspUserSuccessPayload,
): GetCspUserSuccessAction => ({
  type: GET_CSP_USER_SUCCESS,
  payload,
})
const createGetCspUserFailureAction = (
  response: unknown,
): GetCspUserFailureAction => ({
  type: GET_CSP_USER_FAILURE,
  payload: response,
})

export const getCspUser = (
  userId: string,
): ThunkAction<
  Promise<GetCspUserSuccessAction | GetCspUserFailureAction>,
  RootState,
  void,
  GetCspUserActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetCspUserActions>,
    getState: () => RootState,
  ): Promise<GetCspUserSuccessAction | GetCspUserFailureAction> => {
    dispatch(createGetCspUserInitAction())
    return axios
      .get<GetCspUserSuccessPayload>(
        `/csp/users/${userId}`,
        selectCspAuthedIssuerIdJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createGetCspUserSuccessAction({ ...response.data, id: userId }),
          )
        },
        async (err) => {
          return dispatch(createGetCspUserFailureAction(err.response.data))
        },
      )
  }
}

export const PATCH_ORG_MEMBER_INIT = 'data/users/PATCH_ORG_MEMBER_INIT'
export const PATCH_ORG_MEMBER_SUCCESS = 'data/users/PATCH_ORG_MEMBER_SUCCESS'
export const PATCH_ORG_MEMBER_FAILURE = 'data/users/PATCH_ORG_MEMBER_FAILURE'
export const RESET_PATCH_ORG_MEMBER_ERROR =
  'data/users/RESET_PATCH_ORG_MEMBER_ERROR'

export type PatchOrgMemberSuccessPayload = UserListModel

export type PatchOrgMemberFailurePayload = BackendErrorPayload<string>

export interface PatchOrgMemberInitAction {
  type: typeof PATCH_ORG_MEMBER_INIT
}
export interface PatchOrgMemberSuccessAction {
  type: typeof PATCH_ORG_MEMBER_SUCCESS
  payload: PatchOrgMemberSuccessPayload
}
export interface PatchOrgMemberFailureAction {
  type: typeof PATCH_ORG_MEMBER_FAILURE
  payload: PatchOrgMemberFailurePayload
}

export interface ResetPatchOrgMemberErrorAction {
  type: typeof RESET_PATCH_ORG_MEMBER_ERROR
}

export type PatchOrgMemberActions =
  | PatchOrgMemberInitAction
  | PatchOrgMemberSuccessAction
  | PatchOrgMemberFailureAction
  | ResetPatchOrgMemberErrorAction

const createPatchOrgMemberInitAction = (): PatchOrgMemberInitAction => ({
  type: PATCH_ORG_MEMBER_INIT,
})
const createPatchOrgMemberSuccessAction = (
  payload: PatchOrgMemberSuccessPayload,
): PatchOrgMemberSuccessAction => ({
  type: PATCH_ORG_MEMBER_SUCCESS,
  payload,
})
const createPatchOrgMemberFailureAction = (
  payload: PatchOrgMemberFailurePayload,
): PatchOrgMemberFailureAction => ({
  type: PATCH_ORG_MEMBER_FAILURE,
  payload,
})

export const patchOrgMember = (
  data: PeopleDetailInfoFields,
  id: string,
  closeForm?: () => void,
): ThunkAction<Promise<void>, RootState, void, PatchOrgMemberActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PatchOrgMemberActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPatchOrgMemberInitAction())
    return axios
      .patch<PatchOrgMemberSuccessPayload>(
        `/users/${id}`,
        {
          employeeId: data.employeeId,
          updateEmployeeId: true, // Change if we every send multiple items in this request
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPatchOrgMemberSuccessAction(response.data))
          closeForm?.()
        },
        (err) => {
          dispatch(createPatchOrgMemberFailureAction(err.response.data))
        },
      )
  }
}

export const ADD_OWN_USER = 'data/users/ADD_OWN_USER'
export interface AddOwnUserAction {
  type: typeof ADD_OWN_USER
  payload: string
}
const createAddOwnUserAction = (payload: string): AddOwnUserAction => ({
  type: ADD_OWN_USER,
  payload,
})
